import {
  ChartPatternLabels,
  ChartPatternValue,
  CrossoverLabels,
  CrossoverValue,
  HarmonicLabels,
  HarmonicPatternValue,
  MomentumLabels,
  MomentumValue,
  PatternAPIKey,
  ScannerLabels,
  ScannerValue,
  SubScannerLabels,
  SubScannersValue,
} from "@/components/scanners-page";
import { Button } from "@/components/ui/button";
import { API_URL } from "@/config";
import axios from "axios";
import { CircleHelp, Eye } from "lucide-react";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import RunScanSVG from "./assets/RunScanSVG";
import ViewModal from "../StrategyViewModal";
import { usePrebuiltScannerData } from "@/contexts/PrebuiltScannerDataContext";

type PreBuiltScannerItem = {
  type: ScannerValue;
  pattern: SubScannersValue;
  desc: string;
  keygen: string[];
  title: string;
};

const generateScannerItem = (
  type: ScannerValue,
  pattern: SubScannersValue,
  desc: string = "Scanner pattern description.",
  keygen: string[] = []
): PreBuiltScannerItem => ({
  type,
  pattern,
  desc,
  keygen,
  title: SubScannerLabels[pattern] + " - " + ScannerLabels[type],
});

/**
 * The prebuilt scanners will be added here
 */
const preBuiltScannersList = [
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.GARTLEY,
    "Gartley harmonic patterns are chart formations that use Fibonacci ratios to identify potential reversal zones in financial markets. These patterns help traders anticipate price swings and make informed trading decisions."
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.SHARK,
    "The Shark harmonic pattern, a five-leg reversal pattern, utilizes specific Fibonacci ratios to identify potential trend reversals in financial markets. It is characterized by the unique labeling of its points as O, X, A, B, and C, and is known for its accuracy in predicting price movements."
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.DEEP_CRAB,
    "Deep Crab harmonic patterns, complex chart formations, predict potential price reversals by identifying specific Fibonacci retracement and extension levels within a five-point structure (XABCD). Traders seek these patterns to anticipate turning points in markets, aiming to capitalize on subsequent price movements."
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.CYPHER,
    "Cypher harmonic patterns are reversal formations with precise Fibonacci ratios between price legs, signaling potential trend changes in financial markets."
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.CRAB,
    `Crab harmonic patterns are a type of chart pattern used in technical analysis that helps identify potential reversal points in the market. They are characterized by specific Fibonacci retracements and extensions, forming a distinct "M" or "W" shape. Traders look for buying opportunities near the pattern's completion point, anticipating a price reversal.`
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.BUTTERFLY,
    "Butterfly harmonic patterns are a type of chart pattern that traders use to identify potential reversal points in the market. They are based on Fibonacci numbers and ratios, and they form a butterfly-like shape on the chart. When used correctly, butterfly patterns can be a powerful tool for traders to identify high-probability trading opportunities."
  ),
  generateScannerItem(
    ScannerValue.HARMONIC_PATTERN,
    HarmonicPatternValue.BAT,
    "Bat harmonic patterns, a precise technical analysis tool, utilize Fibonacci ratios to identify potential reversal zones in financial markets. These patterns, resembling a bat's silhouette, help traders pinpoint high-probability entry points for trades."
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.BEAR_FLAG,
    `Bear flag patterns are bearish continuation patterns that signal a potential downside breakout after a period of consolidation. They appear as a small, downward-sloping channel or flag after a sharp price decline (the "pole"). A confirmed breakout below the flag's lower trendline often indicates further price drops, targeting a distance roughly equal to the length of the initial pole.`
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.BEAR_PENNANT,
    `Bear pennant patterns are bearish continuation patterns that signal a potential downside breakout after a period of consolidation. They appear as a small, symmetrical triangle or pennant after a sharp price decline (the "pole"). A confirmed breakout below the pennant's lower trendline often indicates further price drops, targeting a distance roughly equal to the length of the initial pole.`
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.BULL_FLAG,
    `Bull flag patterns are bullish continuation patterns signaling a potential upside breakout after a period of consolidation. They appear as a small, upward-sloping channel or flag following a sharp price increase (the "pole"). A confirmed breakout above the flag's upper trendline often suggests further price gains, targeting a distance roughly equal to the length of the initial pole.`
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.BULL_PENNANT,
    `Bull pennant patterns are bullish continuation patterns that signal a potential upside breakout after a period of consolidation. They appear as a small, symmetrical triangle or pennant after a sharp price increase (the "pole"). A confirmed breakout above the pennant's upper trendline often suggests further price gains, targeting a distance roughly equal to the length of the initial pole.`
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.HEAD_AND_SHOULDERS,
    `The head and shoulders pattern is a reversal pattern signaling a potential trend change, often from bullish to bearish. It consists of three peaks: two shoulders of similar height flanking a higher "head." A break below the "neckline" formed by the lows of the shoulders often confirms the pattern, suggesting a potential downtrend.`
  ),
  generateScannerItem(
    ScannerValue.CHART_PATTERN,
    ChartPatternValue.INV_HEAD_AND_SHOULDERS,
    `The inverse head and shoulders pattern is a bullish reversal pattern appearing at the bottom of a downtrend. It features three troughs, with the middle trough (head) lower than the other two (shoulders). A break above the neckline, drawn across the peaks of the shoulders, signals a potential trend reversal to the upside, with a target price measured by the distance from the neckline to the head.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.MACD_SIGNAL,
    `The MACD (Moving Average Convergence Divergence) signal crossover occurs when the MACD line crosses above or below the signal line, indicating potential changes in price momentum. A bullish crossover suggests increasing upward momentum, signaling a buy opportunity, while a bearish crossover indicates potential selling pressure.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.PRICE_BREAK,
    `Price breakout momentum refers to the significant movement of an asset's price beyond established support or resistance levels, often accompanied by increased trading volume. This phenomenon signals the potential start of a new trend, attracting traders who capitalize on emerging price movements.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.ROC_VOL,
    `The Rate of Change (ROC) is a momentum oscillator that measures the percentage change in an asset's price over a specified period. It indicates the speed of price movements, with positive readings suggesting bullish momentum and negative readings indicating bearish trends. ROC helps traders identify potential trend reversals and market strength.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.STOCH_BREAK,
    `A Stochastic RSI (StochRSI) breakout occurs when the indicator moves beyond the overbought or oversold levels, signaling potential price reversals.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.VOL_BREAK,
    `A price breakout occurs when an asset's price moves beyond established support or resistance levels, often accompanied by increased trading volume. High volume during a breakout confirms its validity, indicating strong market interest and potential continuation of the trend. Low volume breakouts may signal weakness and higher chances of reversal.`
  ),
  generateScannerItem(
    ScannerValue.MOMENTUM,
    MomentumValue.VOL_EMA_BREAK,
    `Volume breakout combined with an EMA breakout occurs when an asset's price surpasses a significant exponential moving average (EMA) level, supported by increased trading volume. This scenario enhances the reliability of the breakout, indicating strong market interest and potential continuation of the trend, while low volume may suggest a false signal`
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.BOLLINGER_CROSS,
    `the current price crosses above the Bollinger Bands' middle line, typically a 20-day simple moving average, it signals a potential bullish trend. This movement suggests increasing momentum and may indicate a reversal or continuation of upward price action.`
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.DEATH_CROSS,
    `Death cross is a bearish technical indicator that occurs when a short-term moving average, typically the 50-day, crosses below a long-term moving average, usually the 200-day. This pattern signals potential trend reversals and market weakness, often prompting traders to consider selling or shorting assets as it indicates a shift from bullish to bearish sentiment.`
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.GOLDEN_CROSSOVER,
    `Golden cross occurs when a short-term moving average, typically the 50-day, crosses above a long-term moving average, usually the 200-day. This bullish signal indicates a potential shift to an upward trend, often attracting traders seeking buying opportunities. It suggests increasing market momentum and positive sentiment.`
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.MACD_CROSSOVER,
    `MACD crossover occurs when the MACD line crosses above or below the signal line, indicating potential changes in momentum. A bullish crossover suggests increasing upward momentum, signaling a buy opportunity, while a bearish crossover indicates potential downward momentum, signaling a sell opportunity. `
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.PARABOLIC_CROSS,
    `Parabolic SAR crossover occurs when the Parabolic SAR indicator switches position relative to the price, signaling potential trend reversals. A bullish crossover happens when the SAR moves below the price, indicating a buy signal, while a bearish crossover occurs when it moves above, suggesting a sell signal.`
  ),
  generateScannerItem(
    ScannerValue.CROSS_OVER,
    CrossoverValue.SHORT_UPTREND,
    `Short uptrend strategy involves identifying upward price movements and entering long positions during pullbacks. Traders can utilize indicators like moving averages or trendlines to confirm the trend.`
  ),
];

const getPreBuiltStrategies = async (selectedScanner: string) => {
  const userInfo = JSON.parse(localStorage.getItem("user-info")!);
  try {
    const url = new URL("get_market_scanner_pre_built_strategies", API_URL);
    url.searchParams.append("type", selectedScanner);
    const res = await axios.post(
      url.toString(),
      {},
      {
        headers: {
          Authorization: `Bearer ${userInfo?.token || ""}`,
        },
      }
    );
    if (res.status === 200) {
      return res?.data;
    }
  } catch (e) {
    console.error(e);
  }
};

const PreBuiltScanners = () => {
  const router = useRouter();
  const { fetchPrebuiltData, prebuiltScannerData } = usePrebuiltScannerData();
  const [fetchOnce, setFetchOnce] = useState(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});
  const [filteredPreBuiltScannersList, setFilteredPreBuiltScannersList] =
    useState(preBuiltScannersList);

  const fetchPreBuiltStrategies = useCallback(async () => {
    if (fetchOnce) {
      setFetchOnce(false);
      try {
        const responses = await Promise.all([
          getPreBuiltStrategies(PatternAPIKey["chart-pattern"]),
          getPreBuiltStrategies(PatternAPIKey["cross-over"]),
          getPreBuiltStrategies(PatternAPIKey["harmonic-pattern"]),
          getPreBuiltStrategies(PatternAPIKey["momentum"]),
        ]);

        const [
          chartPatternsResponse,
          crossoverResponse,
          harmonicResponse,
          momentumResponse,
        ] = responses;

        const responseMap: Record<
          | (typeof PatternAPIKey)["chart-pattern"]
          | (typeof PatternAPIKey)["cross-over"]
          | (typeof PatternAPIKey)["harmonic-pattern"]
          | (typeof PatternAPIKey)["momentum"],
          Record<string, string[] | string>
        > = {
          chart_patterns: chartPatternsResponse ?? {},
          crossover: crossoverResponse ?? {},
          harmonic: harmonicResponse ?? {},
          momentum: momentumResponse ?? {},
        };

        const filteredScanners = preBuiltScannersList.map((scannerItem) => {
          const category = getScannerCategory(scannerItem.type);
          const pattern = scannerItem.pattern;

          // Get valid patterns from responseMap
          const validPatterns = responseMap[category] || {};

          // Extract keygen based on scannerItem.pattern
          const keygen =
            validPatterns[pattern] && Array.isArray(validPatterns[pattern])
              ? validPatterns[pattern]
              : [validPatterns[pattern]];

          return {
            ...scannerItem,
            keygen,
          };
        });

        // Set the filtered list
        setFilteredPreBuiltScannersList(filteredScanners);
        const keygenString = filteredScanners
          .map((scanner) => scanner.keygen.join(","))
          .join(",");

        // Now, fetch prebuilt data using keygenString if necessary
        fetchPrebuiltData(keygenString, false);
      } catch (error) {
        console.error("Error fetching pre-built strategies:", error);
      }
    }
  }, []);

  useEffect(() => {
    fetchPreBuiltStrategies();
  }, []);

  const getScannerCategory = (scannerValue: ScannerValue) => {
    if (scannerValue === ScannerValue.CHART_PATTERN) return "chart_patterns";
    if (scannerValue === ScannerValue.CROSS_OVER) return "crossover";
    if (scannerValue === ScannerValue.HARMONIC_PATTERN) return "harmonic";
    if (scannerValue === ScannerValue.MOMENTUM) return "momentum";
    return ""; // Default to an empty string if no category matches
  };

  const handleRunScan = useCallback((scanner: PreBuiltScannerItem) => {
    const label =
      scanner.type === ScannerValue.HARMONIC_PATTERN
        ? [
            `Bearish ${SubScannerLabels[scanner.pattern]}`,
            `Bullish ${SubScannerLabels[scanner.pattern]}`,
          ]
        : [SubScannerLabels[scanner.pattern]];

    router.push({
      pathname: "/run-scan",
      query: {
        locale: router.query.locale || "en",
        keygen: scanner.keygen,
        scanner: scanner.title,
        label: label,
      },
    });
  }, []);

  const handleScannerStrategyView = useCallback((scannerData: string) => {
    const canvasData = scannerData ? JSON.parse(scannerData) : {};
    setSelected({
      nodes: canvasData?.filter?.((item: any) => item.type === "selectorNode"),
      edges: canvasData?.filter?.((item: any) => item.type === "buttonedge"),
      // symbol: scanner.symbol,
      // timeframe: scanner.timeFrame,
    });
    setShowModal(true);
  }, []);

  return (
    <div className="flex flex-col p-2 gap-4 w-full">
      {filteredPreBuiltScannersList.map((scanner, ind) => {
        return (
          <div
            key={ind}
            className="flex flex-col md:flex-row w-full justify-center "
          >
            <div className="w-full px-4 py-2">
              <div className="flex flex-row gap-2">
                <p className="font-normal">{scanner.title}</p>
                {/* <CircleHelp className="text-blue-500" size={15} /> */}
              </div>
              <p className="font-light text-sm text-muted-foreground px-2">
                {scanner.desc}
              </p>
            </div>
            <div className="flex w-auto flex-row gap-10 justify-center md:justify-end items-center">
              {scanner?.keygen?.map((key, index) => {
                const matchedScannerData = prebuiltScannerData.find(
                  (ele) => ele.keygen === key
                );

                const canvasData = matchedScannerData?.canvas_data || "";

                // Assuming canvasData has some indicator to determine Bullish and Bearish
                const isBullish = canvasData.includes("Bullish"); // Adjust condition as necessary
                const isBearish = canvasData.includes("Bearish"); // Adjust condition as necessary

                return (
                  <div key={index} className="flex flex-col">
                    <Button
                      variant="ghost"
                      onClick={() => handleScannerStrategyView(canvasData)}
                    >
                      <div className="flex text-muted-foreground w-full items-center gap-2">
                        <Eye />
                        <p className="text-xs">View</p>
                        {/* Conditionally render Bullish and Bearish based on length or canvasData */}
                      </div>
                    </Button>
                    <div className="text-xs font-bold text-muted-foreground text-center">
                      {isBearish && <p>Bearish</p>}
                      {isBullish && <p>Bullish</p>}
                    </div>
                  </div>
                );
              })}

              <Button
                variant="secondary"
                onClick={
                  () => handleRunScan(scanner)
                  // () =>router.push(`/scanner/${scanner.type}/${scanner.pattern}`)
                }
              >
                <div className="flex w-full items-center gap-2">
                  {/* <RunScanSVG /> */}
                  <p className="text-xs">Run Scan</p>
                </div>
              </Button>
            </div>
          </div>
        );
      })}
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
      />
    </div>
  );
};

export default PreBuiltScanners;
